import { THEME } from '@common/constants/theme.constant';
import { FieldLabel } from '@common/general-ui/field/field-label.component';
import { Readonly } from '@common/general-ui/readonly.component';
import { SerializedStyles } from '@emotion/react';
import { Radio as AntdRadio } from 'antd';
import React, { useCallback } from 'react';
import { css } from 'twin.macro';

export type IRadioChangeArg = { target: { name: string } };
export type IRadioChangeHandler = (arg: IRadioChangeArg) => void;
export type IRadioBlurHandler = (arg: { type: string; target: { name: string } }) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface IRadioOption<T = any> {
    label: string;
    value: string;
    visible?: boolean;
    data?: T;
}

export interface IRadioProps {
    option: IRadioOption;
    onChange?: IRadioChangeHandler;
    onBlur?: IRadioBlurHandler;
    className?: string;
    renderLabel?: (value: string) => JSX.Element;
    readOnly?: boolean;
    name?: string;
}

export const Radio = (props: IRadioProps): JSX.Element => {
    const {
        option: { value, label },
        onChange,
        onBlur,
        className,
        renderLabel,
    } = props;

    const handleChange = useCallback(() => {
        onChange && onChange({ target: { name: value } });
        onBlur && onBlur({ type: 'blur', target: { name: value } });
    }, [onChange, value, onBlur]);

    const handleClick = useCallback(() => {
        onChange && onChange({ target: { name: value } });
        onBlur && onBlur({ type: 'blur', target: { name: value } });
    }, [onChange, value, onBlur]);

    return (
        <div className={className} data-testid={`radio-option-${value}`} tw="relative">
            {props.readOnly && <Readonly />}
            <AntdRadio css={styles.radio(!!props.readOnly)} value={value} onChange={handleChange} name={props.name} />
            <FieldLabel
                label={renderLabel ? renderLabel(value) : label}
                onLabelClick={handleClick}
                tw="cursor-pointer inline font-normal"
            />
        </div>
    );
};

const styles = {
    radio: (readOnly: boolean): (SerializedStyles | boolean)[] => [
        css`
            .ant-radio:focus-within {
                .ant-radio-inner {
                    outline-color: transparent;
                    outline-style: none;
                    ${THEME.BOX_SHADOW_GREEN} !important;
                }
            }
        `,
        readOnly &&
            css`
                && .ant-radio-inner {
                    border-color: ${THEME.READONLY_COLOR};
                }
                && .ant-radio-inner:after {
                    background-color: ${THEME.READONLY_COLOR};
                }
            `,
    ],
};
